'use client'

import { BaseRenderer, EnvironmentRenderer, BackgroundRenderer } from '../../utils/environmentRenderer'

export class VeniceBackgroundRenderer implements BackgroundRenderer {

drawBackground(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number, canvasHeight: number): void {
	// Ciel vénitien avec teintes dorées
	EnvironmentRenderer.drawSkyGradient(ctx, cameraX, canvasWidth, canvasHeight, '#FFE4B5', '#87CEFA')

	// Dessiner l'arrière-plan vénitien par couches
	this.drawVeniceLandmarks(ctx, cameraX, canvasWidth)
	this.drawVeniceBuildings(ctx, cameraX, canvasWidth)
	this.drawVeniceCanals(ctx, cameraX, canvasWidth)
	this.drawVeniceClouds(ctx, cameraX, canvasWidth)
}

drawGround(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number, canvasHeight: number): void {
	// Sol vénitien en pierre d'Istrie
	EnvironmentRenderer.drawTexturedGround(
	ctx, cameraX, canvasWidth, 360, 40, '#F5F5DC', '#E6E6FA', 6
	)
}

private drawVeniceLandmarks(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Campanile de San Marco (tous les 2500 pixels) - très loin
	for (let x = Math.floor(startX / 2500) * 2500; x <= endX; x += 2500) {
	this.drawCampanile(ctx, x + 400, 150)
	}

	// Basilique Saint-Marc (tous les 2000 pixels, décalé) - loin
	for (let x = Math.floor(startX / 2000) * 2000; x <= endX; x += 2000) {
	this.drawBasilica(ctx, x + 600, 200)
	}

	// Palais des Doges (tous les 1800 pixels) - moyen plan
	for (let x = Math.floor(startX / 1800) * 1800; x <= endX; x += 1800) {
	this.drawDogePalace(ctx, x + 300, 180)
	}
}

private drawVeniceBuildings(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Palazzos vénitiens variés
	for (let x = Math.floor(startX / 180) * 180; x <= endX; x += 180) {
	const variation = Math.sin(x * 0.007) * 15
	this.drawVenicePalazzo(ctx, x + 30, 250 + variation)
	}

	// Maisons vénitiennes plus petites
	for (let x = Math.floor(startX / 120) * 120; x <= endX; x += 120) {
	const colorVariation = Math.floor(Math.abs(Math.sin(x * 0.01)) * 4)
	this.drawVeniceHouse(ctx, x + 60, 290, colorVariation)
	}
}

private drawVeniceCanals(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Ponts vénitiens
	for (let x = Math.floor(startX / 400) * 400; x <= endX; x += 400) {
	this.drawVeniceBridge(ctx, x + 150, 320)
	}

	// Gondoles dans les canaux (éléments décoratifs)
	for (let x = Math.floor(startX / 600) * 600; x <= endX; x += 600) {
	this.drawGondola(ctx, x + 200, 350)
	}

	// Poteaux de gondole (palina)
	for (let x = Math.floor(startX / 200) * 200; x <= endX; x += 200) {
	this.drawGondolaPole(ctx, x + 100, 340)
	}
}

private drawVeniceClouds(ctx: CanvasRenderingContext2D, cameraX: number, canvasWidth: number): void {
	const startX = cameraX - 300
	const endX = cameraX + canvasWidth + 300

	// Nuages dorés de Venise
	for (let x = Math.floor(startX / 350) * 350; x <= endX; x += 350) {
	this.drawGoldenCloud(ctx, x + 120, 30 + (Math.sin(x * 0.004) * 12))
	}

	// Petits nuages d'appoint
	for (let x = Math.floor(startX / 220) * 220; x <= endX; x += 220) {
	BaseRenderer.drawSmallCloud(ctx, x + 80, 20 + (Math.sin(x * 0.008) * 6), 0.7)
	}
}

// Dessiner le Campanile de San Marco
private drawCampanile(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const scale = 0.6

	// Tour principale en brique rouge
	ctx.fillStyle = 'rgba(205, 92, 92, 0.8)'
	ctx.fillRect(x - 8 * scale, y, 16 * scale, 120 * scale)

	// Partie supérieure blanche
	ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
	ctx.fillRect(x - 8 * scale, y - 20 * scale, 16 * scale, 20 * scale)

	// Pyramide dorée au sommet
	ctx.fillStyle = 'rgba(255, 215, 0, 0.8)'
	ctx.beginPath()
	ctx.moveTo(x - 8 * scale, y - 20 * scale)
	ctx.lineTo(x, y - 35 * scale)
	ctx.lineTo(x + 8 * scale, y - 20 * scale)
	ctx.closePath()
	ctx.fill()

	// Ange doré au sommet
	ctx.fillStyle = 'rgba(255, 215, 0, 0.9)'
	ctx.fillRect(x - 1 * scale, y - 38 * scale, 2 * scale, 6 * scale)

	// Arcades vénitiennes (détails simplifiés)
	ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
	for (let i = 0; i < 3; i++) {
	ctx.fillRect(x - 6 * scale, y + 20 * scale + i * 25 * scale, 4 * scale, 8 * scale)
	ctx.fillRect(x + 2 * scale, y + 20 * scale + i * 25 * scale, 4 * scale, 8 * scale)
	}

	ctx.restore()
}

// Dessiner la Basilique Saint-Marc (simplifiée)
private drawBasilica(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const scale = 0.7

	// Corps principal doré
	ctx.fillStyle = 'rgba(255, 215, 0, 0.7)'
	ctx.fillRect(x, y, 60 * scale, 40 * scale)

	// Coupoles byzantines
	ctx.fillStyle = 'rgba(255, 140, 0, 0.8)'
	// Coupole centrale
	ctx.beginPath()
	ctx.arc(x + 30 * scale, y, 12 * scale, 0, Math.PI, true)
	ctx.fill()

	// Coupoles latérales
	ctx.beginPath()
	ctx.arc(x + 15 * scale, y + 5 * scale, 8 * scale, 0, Math.PI, true)
	ctx.fill()
	ctx.beginPath()
	ctx.arc(x + 45 * scale, y + 5 * scale, 8 * scale, 0, Math.PI, true)
	ctx.fill()

	// Portail principal
	ctx.fillStyle = 'rgba(139, 69, 19, 0.8)'
	ctx.fillRect(x + 25 * scale, y + 20 * scale, 10 * scale, 20 * scale)

	// Détails dorés
	ctx.fillStyle = 'rgba(255, 215, 0, 0.9)'
	ctx.fillRect(x + 5 * scale, y + 10 * scale, 50 * scale, 2 * scale)

	ctx.restore()
}

// Dessiner le Palais des Doges
private drawDogePalace(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const scale = 0.8

	// Corps principal rose vénitien
	ctx.fillStyle = 'rgba(255, 182, 193, 0.8)'
	ctx.fillRect(x, y, 70 * scale, 50 * scale)

	// Arcades du rez-de-chaussée
	ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
	ctx.fillRect(x, y + 35 * scale, 70 * scale, 15 * scale)

	// Loggia supérieure
	ctx.fillStyle = 'rgba(255, 182, 193, 0.8)'
	ctx.fillRect(x, y + 15 * scale, 70 * scale, 20 * scale)

	// Motif losange (pattern vénitien caractéristique)
	ctx.fillStyle = 'rgba(255, 255, 255, 0.7)'
	for (let i = 0; i < 8; i++) {
	for (let j = 0; j < 3; j++) {
		if ((i + j) % 2 === 0) {
		ctx.fillRect(x + i * 8 * scale, y + 15 * scale + j * 6 * scale, 4 * scale, 3 * scale)
		}
	}
	}

	// Toit en forme de dôme
	ctx.fillStyle = 'rgba(255, 140, 0, 0.8)'
	ctx.beginPath()
	ctx.moveTo(x, y)
	ctx.quadraticCurveTo(x + 35 * scale, y - 10 * scale, x + 70 * scale, y)
	ctx.lineTo(x + 70 * scale, y + 5 * scale)
	ctx.lineTo(x, y + 5 * scale)
	ctx.closePath()
	ctx.fill()

	ctx.restore()
}

// Dessiner un palazzo vénitien
private drawVenicePalazzo(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()
	const height = 70 + (Math.sin(x * 0.015) * 12)

	// Couleurs typiques des palazzos vénitiens
	const colors = ['#FFB6C1', '#F0E68C', '#DDA0DD', '#98FB98']
	const colorIndex = Math.floor(Math.abs(Math.sin(x * 0.02)) * colors.length)

	// Façade principale
	ctx.fillStyle = colors[colorIndex]
	ctx.fillRect(x, y, 45, height)

	// Fenêtres gothiques vénitiennes
	ctx.fillStyle = '#4682B4'
	for (let row = 0; row < 3; row++) {
	for (let col = 0; col < 2; col++) {
		const winX = x + 8 + col * 20
		const winY = y + 10 + row * 18

		// Fenêtre rectangulaire
		ctx.fillRect(winX, winY, 8, 12)

		// Arc gothique au sommet
		ctx.beginPath()
		ctx.arc(winX + 4, winY, 4, 0, Math.PI, true)
		ctx.fill()
	}
	}

	// Balcon vénitien central
	ctx.fillStyle = '#2F4F4F'
	ctx.fillRect(x + 5, y + 25, 35, 2)

	// Garde-corps du balcon
	ctx.strokeStyle = '#2F4F4F'
	ctx.lineWidth = 1
	for (let i = 0; i < 7; i++) {
	ctx.beginPath()
	ctx.moveTo(x + 8 + i * 5, y + 20)
	ctx.lineTo(x + 8 + i * 5, y + 25)
	ctx.stroke()
	}

	ctx.restore()
}

// Dessiner une maison vénitienne
private drawVeniceHouse(ctx: CanvasRenderingContext2D, x: number, y: number, colorVariation: number): void {
	ctx.save()

	const colors = ['#FFE4E1', '#F0F8FF', '#FFF8DC', '#F5FFFA']
	const height = 50 + (Math.sin(x * 0.03) * 8)

	// Façade
	ctx.fillStyle = colors[colorVariation]
	ctx.fillRect(x, y, 30, height)

	// Toit vénitien
	ctx.fillStyle = '#CD853F'
	ctx.fillRect(x - 2, y - 5, 34, 8)

	// Fenêtres simples
	ctx.fillStyle = '#4169E1'
	ctx.fillRect(x + 5, y + 10, 6, 8)
	ctx.fillRect(x + 19, y + 10, 6, 8)
	ctx.fillRect(x + 12, y + 25, 6, 8)

	// Porte
	ctx.fillStyle = '#8B4513'
	ctx.fillRect(x + 12, y + 35, 6, height - 35)

	ctx.restore()
}

// Dessiner un pont vénitien
private drawVeniceBridge(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()

	// Arche du pont
	ctx.fillStyle = '#F5F5DC'
	ctx.fillRect(x, y + 20, 60, 15)

	// Arc de l'arche
	ctx.fillStyle = '#87CEFA' // Couleur de l'eau pour créer l'ouverture
	ctx.beginPath()
	ctx.arc(x + 30, y + 35, 25, 0, Math.PI, true)
	ctx.fill()

	// Parapet du pont
	ctx.fillStyle = '#F5F5DC'
	ctx.fillRect(x + 5, y + 10, 50, 5)

	// Garde-corps
	ctx.strokeStyle = '#696969'
	ctx.lineWidth = 2
	ctx.beginPath()
	ctx.moveTo(x + 10, y + 10)
	ctx.lineTo(x + 50, y + 10)
	ctx.stroke()

	// Escaliers
	ctx.fillStyle = '#E0E0E0'
	for (let i = 0; i < 3; i++) {
	ctx.fillRect(x - 5 - i * 3, y + 15 + i * 5, 8, 5)
	ctx.fillRect(x + 60 + i * 3, y + 15 + i * 5, 8, 5)
	}

	ctx.restore()
}

// Dessiner une gondole
private drawGondola(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()

	// Coque de la gondole
	ctx.fillStyle = '#000000'
	ctx.fillRect(x, y, 40, 8)

	// Proue caractéristique (ferro)
	ctx.fillStyle = '#2F4F4F'
	ctx.fillRect(x + 38, y - 5, 3, 18)

	// Détails décoratifs de la proue
	ctx.fillStyle = '#FFD700'
	ctx.fillRect(x + 40, y - 3, 1, 4)
	ctx.fillRect(x + 40, y + 2, 1, 4)
	ctx.fillRect(x + 40, y + 7, 1, 4)

	// Gondolier (simplifié)
	if (Math.random() > 0.5) { // Parfois une gondole vide
	ctx.fillStyle = '#0000FF'
	ctx.fillRect(x + 15, y - 10, 6, 10) // Corps

	ctx.fillStyle = '#FFDBAC'
	ctx.fillRect(x + 16, y - 15, 4, 5) // Tête

	// Rame
	ctx.strokeStyle = '#8B4513'
	ctx.lineWidth = 2
	ctx.beginPath()
	ctx.moveTo(x + 18, y - 8)
	ctx.lineTo(x + 25, y - 15)
	ctx.stroke()
	}

	ctx.restore()
}

// Dessiner un poteau de gondole (palina)
private drawGondolaPole(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()

	// Poteau principal
	ctx.fillStyle = '#8B4513'
	ctx.fillRect(x + 8, y - 20, 4, 60)

	// Bandes de métal
	ctx.fillStyle = '#2F4F4F'
	ctx.fillRect(x + 7, y - 15, 6, 2)
	ctx.fillRect(x + 7, y - 5, 6, 2)
	ctx.fillRect(x + 7, y + 10, 6, 2)

	// Base immergée (suggérée)
	ctx.fillStyle = 'rgba(139, 69, 19, 0.5)'
	ctx.fillRect(x + 8, y + 20, 4, 20)

	ctx.restore()
}

// Dessiner un nuage doré vénitien
private drawGoldenCloud(ctx: CanvasRenderingContext2D, x: number, y: number): void {
	ctx.save()

	// Nuage avec teinte dorée
	ctx.fillStyle = 'rgba(255, 248, 220, 0.9)'
	ctx.fillRect(x, y + 5, 35, 12)
	ctx.fillRect(x + 5, y, 25, 22)
	ctx.fillRect(x + 10, y + 3, 30, 16)

	// Reflets dorés
	ctx.fillStyle = 'rgba(255, 215, 0, 0.3)'
	ctx.fillRect(x + 8, y + 8, 20, 6)

	ctx.restore()
}
}

// Fonction pour dessiner les obstacles vénitiens (gondoliers pressés)
export const drawVeniceObstacle = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void => {
ctx.save()

// Gondole noire traditionnelle
ctx.fillStyle = '#000000'
ctx.fillRect(x, y + height - 8, width, 8)

// Proue caractéristique avec ferro
ctx.fillStyle = '#2F4F4F'
ctx.fillRect(x + width - 3, y + height - 15, 4, 20)

// Détails dorés du ferro
ctx.fillStyle = '#FFD700'
for (let i = 0; i < 6; i++) {
	ctx.fillRect(x + width, y + height - 12 + i * 2, 2, 1)
}

// Gondolier énervé
const gondolierX = x + width/2

// Corps penché en avant (posture agressive)
ctx.fillStyle = '#FF6B35' // Maillot rayé vénitien
ctx.fillRect(gondolierX - 3, y + height - 25, 6, 10)

// Rayures du maillot
ctx.fillStyle = '#FFFFFF'
for (let i = 0; i < 3; i++) {
	ctx.fillRect(gondolierX - 3, y + height - 23 + i * 3, 6, 1)
}

// Tête avec béret vénitien
ctx.fillStyle = '#FFDBAC'
ctx.beginPath()
ctx.arc(gondolierX, y + height - 30, 3, 0, 2 * Math.PI)
ctx.fill()

// Béret noir
ctx.fillStyle = '#000000'
ctx.beginPath()
ctx.arc(gondolierX, y + height - 32, 4, Math.PI, 2 * Math.PI)
ctx.fill()

// Expression de colère
ctx.fillStyle = '#000000'
ctx.fillRect(gondolierX - 2, y + height - 30, 1, 1) // Œil gauche
ctx.fillRect(gondolierX + 1, y + height - 30, 1, 1) // Œil droit

// Sourcils froncés
ctx.strokeStyle = '#8B4513'
ctx.lineWidth = 1
ctx.beginPath()
ctx.moveTo(gondolierX - 2, y + height - 32)
ctx.lineTo(gondolierX - 1, y + height - 31)
ctx.stroke()
ctx.beginPath()
ctx.moveTo(gondolierX + 2, y + height - 32)
ctx.lineTo(gondolierX + 1, y + height - 31)
ctx.stroke()

// Bouche qui crie
ctx.fillStyle = '#8B0000'
ctx.fillRect(gondolierX - 1, y + height - 27, 2, 2)

// Rame levée de manière menaçante
ctx.strokeStyle = '#8B4513'
ctx.lineWidth = 3
ctx.beginPath()
ctx.moveTo(gondolierX - 2, y + height - 25) // Main
ctx.lineTo(gondolierX - 8, y + height - 35) // Bout de la rame
ctx.stroke()

// Palette de la rame
ctx.fillStyle = '#DEB887'
ctx.fillRect(gondolierX - 10, y + height - 37, 4, 8)

// Bras tenant la rame
ctx.strokeStyle = '#FFDBAC'
ctx.lineWidth = 2
ctx.beginPath()
ctx.moveTo(gondolierX - 1, y + height - 22) // Épaule
ctx.lineTo(gondolierX - 2, y + height - 25) // Main
ctx.stroke()

// Jambes écartées pour l'équilibre
ctx.strokeStyle = '#000080' // Pantalon bleu vénitien
ctx.lineWidth = 2
ctx.beginPath()
ctx.moveTo(gondolierX, y + height - 15) // Taille
ctx.lineTo(gondolierX - 3, y + height - 5) // Pied gauche
ctx.stroke()
ctx.beginPath()
ctx.moveTo(gondolierX, y + height - 15) // Taille
ctx.lineTo(gondolierX + 3, y + height - 5) // Pied droit
ctx.stroke()

// Lignes de colère autour de la tête
ctx.strokeStyle = '#FF4444'
ctx.lineWidth = 1
for (let i = 0; i < 4; i++) {
	const angle = (i * Math.PI) / 8
	ctx.beginPath()
	ctx.moveTo(gondolierX + Math.cos(angle) * 5, y + height - 30 + Math.sin(angle) * 5)
	ctx.lineTo(gondolierX + Math.cos(angle) * 7, y + height - 30 + Math.sin(angle) * 7)
	ctx.stroke()
}

// Gouttes de sueur
ctx.fillStyle = '#87CEEB'
ctx.beginPath()
ctx.arc(gondolierX + 2, y + height - 28, 1, 0, 2 * Math.PI)
ctx.fill()

ctx.restore()
}

// Fonction pour dessiner un spritz (collectible vénitien)
export const drawSpritz = (ctx: CanvasRenderingContext2D, x: number, y: number): void => {
ctx.save()

// Verre à spritz
ctx.fillStyle = '#FFE4E1'
ctx.fillRect(x + 8, y + 5, 8, 15) // Corps du verre
ctx.fillRect(x + 6, y + 18, 12, 3) // Base élargie

// Liquide orange du spritz
ctx.fillStyle = '#FF8C00'
ctx.fillRect(x + 9, y + 8, 6, 10)

// Bulles
ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
ctx.fillRect(x + 10, y + 10, 1, 1)
ctx.fillRect(x + 13, y + 12, 1, 1)
ctx.fillRect(x + 11, y + 15, 1, 1)

// Tranche d'orange
ctx.fillStyle = '#FFA500'
ctx.beginPath()
ctx.arc(x + 12, y + 6, 2, 0, Math.PI)
ctx.fill()

// Détails de la tranche
ctx.strokeStyle = '#FF8C00'
ctx.lineWidth = 1
ctx.beginPath()
ctx.moveTo(x + 10, y + 6)
ctx.lineTo(x + 14, y + 6)
ctx.stroke()

// Reflet sur le verre
ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
ctx.fillRect(x + 9, y + 8, 1, 8)

ctx.restore()
}
